import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Radar, Plus, Trash2, Download, Printer, Loader2, AlertTriangle, ClipboardCheck, X, ChevronDown } from 'lucide-react';
import { devices as devApi, apiCustomers } from '../../lib/api';
import { toCSV, downloadFile, printElement } from '../../lib/importExport';
import { toast } from 'sonner';
import { todayISO } from '../../lib/dates';

import { printDeviceLabels } from '../../lib/deviceLabels';
import { QrCode } from 'lucide-react';
const DEVICE_TYPES = ['Exterior Bait Station', 'Interior Bait Station', 'Multi-Catch Trap', 'Snap Trap', 'Glue Board', 'Insect Light Trap', 'Pheromone Monitor', 'Other'];
const ACTIVITY = ['None', 'Low', 'Moderate', 'High'];
const CONDITIONS = ['Good', 'Needs attention', 'Damaged'];
const ACTIONS = ['Rebaited', 'Cleaned', 'Replaced', 'Relocated', 'None'];
const STATUSES = ['Active', 'Removed', 'Damaged', 'Missing'];

const actColor = (a) => ({ None: 'bg-slate-100 text-slate-600', Low: 'bg-blue-100 text-blue-700', Moderate: 'bg-amber-100 text-amber-700', High: 'bg-red-100 text-red-700' }[a] || 'bg-slate-100 text-slate-600');
const blankDevice = () => ({ label: '', type: 'Exterior Bait Station', location: '', installDate: todayISO(), status: 'Active', notes: '' });

export default function DevicesPage() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(blankDevice());
  const [saving, setSaving] = useState(false);
  const [inspectFor, setInspectFor] = useState(null);   // device id being inspected
  const [insp, setInsp] = useState({ activity: 'None', action: 'Rebaited', condition: 'Good', findings: '', notes: '' });
  const [rawDevices, setRawDevices] = useState([]);

  const load = useCallback(async () => {
    if (!customerId) { setReport(null); setRawDevices([]); return; }
    setLoading(true);
    try {
      const [rep, devs] = await Promise.all([devApi.report({ customerId }), devApi.list(customerId)]);
      setReport(rep);
      setRawDevices(Array.isArray(devs) ? devs : []);
    } catch (e) { toast.error('Could not load devices'); }
    finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { apiCustomers.list().then((c) => setCustomers(Array.isArray(c) ? c : [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const custName = (customers.find((c) => c.id === customerId) || {});
  const customerName = custName.name || custName.customerName || '';

  const addDevice = async () => {
    if (!form.label.trim()) { toast.error('Enter a device label/number'); return; }
    setSaving(true);
    try {
      await devApi.create({ ...form, customerId, customerName });
      toast.success('Device added');
      setForm(blankDevice()); setShowAdd(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add device'); }
    finally { setSaving(false); }
  };

  const logInspection = async (deviceId) => {
    setSaving(true);
    try {
      await devApi.inspect({ deviceId, customerId, ...insp });
      toast.success('Inspection logged');
      setInspectFor(null);
      setInsp({ activity: 'None', action: 'Rebaited', condition: 'Good', findings: '', notes: '' });
      load();
    } catch (e) { toast.error('Could not log inspection'); }
    finally { setSaving(false); }
  };

  const removeDevice = async (d) => {
    const dev = rawDevices.find((x) => x.label === d.label);
    if (!dev) return;
    if (!window.confirm(`Remove device ${d.label} and its inspection history?`)) return;
    try { await devApi.remove(dev.id); toast.success('Removed'); load(); }
    catch { toast.error('Could not remove'); }
  };

  const idOfLabel = (label) => (rawDevices.find((x) => x.label === label) || {}).id;

  const exportCSV = () => {
    if (!report || !report.devices.length) { toast.error('Nothing to export'); return; }
    const cols = [
      { key: 'label', header: 'Device' }, { key: 'type', header: 'Type' },
      { key: 'location', header: 'Location' }, { key: 'status', header: 'Status' },
      { key: 'lastActivity', header: 'Last Activity' }, { key: 'lastCondition', header: 'Condition' },
      { key: 'lastInspected', header: 'Last Inspected' },
    ];
    downloadFile(`device-log-${customerName || customerId}.csv`, toCSV(report.devices, cols));
  };

  const counts = report?.counts || { total: 0, active: 0, byActivity: { None: 0, Low: 0, Moderate: 0, High: 0 } };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Radar className="w-6 h-6 text-[#8BC53F]" />
          <h1 className="text-2xl font-extrabold text-slate-900">Device Tracking</h1>
        </div>
        {customerId && (
          <div className="flex gap-2">
            <Button variant="outline" disabled={!rawDevices.length} className="gap-1.5" data-testid="print-labels-btn"
              onClick={async () => { if (!(await printDeviceLabels(rawDevices.filter((d) => d.status !== 'Removed'), customerName))) toast.error('Allow pop-ups to print labels'); }}>
              <QrCode className="w-4 h-4" /> Print QR labels
            </Button>
            <Button onClick={() => setShowAdd((s) => !s)} className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white font-semibold gap-1.5" data-testid="add-device-btn">
              <Plus className="w-4 h-4" /> Add Device
            </Button>
          </div>
        )}
      </div>
      <p className="text-slate-500 text-sm mt-1">Track bait stations, traps, and monitors per site - with inspection logs and an audit-ready activity report.</p>

      <div className="mt-4 max-w-sm">
        <label className="text-xs font-medium text-slate-600">Site / Customer</label>
        <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 bg-white text-sm" data-testid="device-customer">
          <option value="">- select a site -</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.customerName || c.id}</option>)}
        </select>
      </div>

      {showAdd && customerId && (
        <Card className="mt-4 p-5 border-slate-200" data-testid="device-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-900">Add a device</h2>
            <button onClick={() => setShowAdd(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-slate-600">Device # / label *</label><Input value={form.label} onChange={(e) => setForm((f2) => ({ ...f2, label: e.target.value }))} placeholder="EBS-01" className="mt-1 h-10" /></div>
            <div><label className="text-xs font-medium text-slate-600">Type</label>
              <select value={form.type} onChange={(e) => setForm((f2) => ({ ...f2, type: e.target.value }))} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white text-sm">
                {DEVICE_TYPES.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Location</label><Input value={form.location} onChange={(e) => setForm((f2) => ({ ...f2, location: e.target.value }))} placeholder="NE corner, dock door 3" className="mt-1 h-10" /></div>
            <div><label className="text-xs font-medium text-slate-600">Install date</label><Input type="date" value={form.installDate} onChange={(e) => setForm((f2) => ({ ...f2, installDate: e.target.value }))} className="mt-1 h-10" /></div>
            <div><label className="text-xs font-medium text-slate-600">Status</label>
              <select value={form.status} onChange={(e) => setForm((f2) => ({ ...f2, status: e.target.value }))} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Notes</label><Input value={form.notes} onChange={(e) => setForm((f2) => ({ ...f2, notes: e.target.value }))} className="mt-1 h-10" /></div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={addDevice} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white font-semibold">{saving ? 'Saving...' : 'Save device'}</Button>
            <Button onClick={() => setShowAdd(false)} variant="outline">Cancel</Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : !customerId ? (
        <p className="mt-10 text-center text-slate-400 text-sm">Select a site above to view its devices.</p>
      ) : report ? (
        <div className="mt-5">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <h2 className="font-bold text-slate-900">{report.customerName || customerName} - Device Log</h2>
            <div className="flex items-center gap-2">
              <button onClick={exportCSV} className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium flex items-center gap-1.5"><Download className="w-4 h-4" />CSV</button>
              <button onClick={() => printElement('#dev-report')} className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium flex items-center gap-1.5"><Printer className="w-4 h-4" />Print</button>
            </div>
          </div>

          <div id="dev-report">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs text-slate-400">Total</div><div className="text-xl font-extrabold text-slate-900">{counts.total}</div></div>
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs text-slate-400">Active</div><div className="text-xl font-extrabold text-slate-900">{counts.active}</div></div>
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3"><div className="text-xs text-blue-500">Low</div><div className="text-xl font-extrabold text-blue-700">{counts.byActivity.Low}</div></div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3"><div className="text-xs text-amber-500">Moderate</div><div className="text-xl font-extrabold text-amber-700">{counts.byActivity.Moderate}</div></div>
              <div className="rounded-xl border border-red-200 bg-red-50 p-3"><div className="text-xs text-red-500">High</div><div className="text-xl font-extrabold text-red-700">{counts.byActivity.High}</div></div>
            </div>

            {report.attention.length > 0 && (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="device-attention">
                <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm"><AlertTriangle className="w-4 h-4" />Needs attention ({report.attention.length})</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {report.attention.map((d, i) => <span key={i} className="text-xs bg-white border border-amber-200 rounded-full px-2.5 py-1 text-amber-800">{d.label} - {d.lastActivity}</span>)}
                </div>
              </div>
            )}

            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-3 font-semibold">Device</th>
                    <th className="py-2 pr-3 font-semibold">Type</th>
                    <th className="py-2 pr-3 font-semibold">Location</th>
                    <th className="py-2 pr-3 font-semibold">Activity</th>
                    <th className="py-2 pr-3 font-semibold">Condition</th>
                    <th className="py-2 pr-3 font-semibold">Last Insp.</th>
                    <th className="py-2 pr-3 font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {report.devices.map((d, i) => (
                    <React.Fragment key={i}>
                      <tr className="border-b border-slate-50">
                        <td className="py-2 pr-3 font-medium text-slate-800">{d.label}</td>
                        <td className="py-2 pr-3 text-slate-600">{d.type}</td>
                        <td className="py-2 pr-3 text-slate-600">{d.location || '-'}</td>
                        <td className="py-2 pr-3"><Badge className={`${actColor(d.lastActivity)} border-0`}>{d.lastActivity}</Badge></td>
                        <td className="py-2 pr-3 text-slate-600">{d.lastCondition}</td>
                        <td className="py-2 pr-3 text-slate-500">{d.lastInspected || '-'}</td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">
                          <button onClick={() => setInspectFor(inspectFor === idOfLabel(d.label) ? null : idOfLabel(d.label))} className="text-[#6FA82C] font-semibold text-xs inline-flex items-center gap-1"><ClipboardCheck className="w-3.5 h-3.5" />Inspect</button>
                          <button onClick={() => removeDevice(d)} className="ml-2 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                        </td>
                      </tr>
                      {inspectFor === idOfLabel(d.label) && (
                        <tr className="bg-slate-50">
                          <td colSpan={7} className="p-3">
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                              <div><label className="text-xs text-slate-500">Activity</label>
                                <select value={insp.activity} onChange={(e) => setInsp((s) => ({ ...s, activity: e.target.value }))} className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 bg-white text-sm">{ACTIVITY.map((a) => <option key={a} value={a}>{a}</option>)}</select>
                              </div>
                              <div><label className="text-xs text-slate-500">Action</label>
                                <select value={insp.action} onChange={(e) => setInsp((s) => ({ ...s, action: e.target.value }))} className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 bg-white text-sm">{ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}</select>
                              </div>
                              <div><label className="text-xs text-slate-500">Condition</label>
                                <select value={insp.condition} onChange={(e) => setInsp((s) => ({ ...s, condition: e.target.value }))} className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 bg-white text-sm">{CONDITIONS.map((a) => <option key={a} value={a}>{a}</option>)}</select>
                              </div>
                              <div><label className="text-xs text-slate-500">Findings</label><Input value={insp.findings} onChange={(e) => setInsp((s) => ({ ...s, findings: e.target.value }))} className="mt-1 h-9" /></div>
                            </div>
                            <div className="flex gap-2 mt-2">
                              <Button onClick={() => logInspection(idOfLabel(d.label))} disabled={saving} size="sm" className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white">{saving ? 'Saving...' : 'Save inspection'}</Button>
                              <Button onClick={() => setInspectFor(null)} size="sm" variant="outline">Cancel</Button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {report.devices.length === 0 && (
                    <tr><td colSpan={7} className="py-10 text-center text-slate-400">No devices yet. Add your first one above.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
